import { Undo } from "@mui/icons-material"
import AddIcon from "@mui/icons-material/Add"
import CancelIcon from "@mui/icons-material/Cancel"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import PrintIcon from "@mui/icons-material/Print"
import SaveIcon from "@mui/icons-material/Save"
import { Button, ButtonProps, IconButton, IconButtonProps } from "@mui/material"
import React from "react"

type ButtonType = "add" | "edit" | "save" | "delete" | "print" | "cancel" | "custom" | "reset"

interface StyledButtonProps extends Omit<ButtonProps, "className"> {
  buttonType?: ButtonType
  customIcon?: React.ReactNode
  customText?: string
  iconOnly?: boolean
}

const getButtonContent = (
  type: ButtonType,
  customIcon?: React.ReactNode,
  customText?: string,
  iconOnly?: boolean
) => {
  switch (type) {
    case "add":
      return { icon: <AddIcon />, text: iconOnly ? "" : customText || "Hinzufügen" }
    case "edit":
      return { icon: <EditIcon />, text: iconOnly ? "" : customText || "Bearbeiten" }
    case "save":
      return { icon: <SaveIcon />, text: iconOnly ? "" : customText || "Speichern" }
    case "delete":
      return { icon: <DeleteIcon />, text: iconOnly ? "" : customText || "Löschen" }
    case "print":
      return { icon: <PrintIcon />, text: iconOnly ? "" : customText || "Drucken" }
    case "cancel":
      return { icon: <CancelIcon />, text: iconOnly ? "" : customText || "Abbrechen" }
    case "custom":
      return { icon: customIcon, text: iconOnly ? "" : customText }
    case "reset":
      return { icon: <Undo />, text: iconOnly ? "" : customText || "Zurücksetzen" }
    default:
      return { icon: null, text: "" }
  }
}

const getButtonClassName = (type: ButtonType): string => {
  return `styled-button styled-button-${type}`
}

export const StyledButton: React.FC<StyledButtonProps> = ({
  buttonType = "custom",
  customIcon,
  customText,
  iconOnly = false,
  children,
  ...props
}) => {
  const { icon, text } = getButtonContent(buttonType, customIcon, customText, iconOnly)
  const className = getButtonClassName(buttonType)

  if (iconOnly) {
    // Use IconButton for icon-only buttons
    const iconButtonProps = props as Omit<IconButtonProps, "children">
    return (
      <IconButton className={className} {...iconButtonProps}>
        {icon}
      </IconButton>
    )
  }

  // Use regular Button for buttons with text
  return (
    <Button
      className={className}
      startIcon={icon}
      variant="contained"
      {...props}
      color={
        buttonType === "cancel" ? "secondary" : buttonType === "reset" ? "secondary" : "primary"
      }
    >
      {text || children}
    </Button>
  )
}
